<template>
  <TestFormPanel
    v-if="test"
    :test="test"
    :panel-title="test.label"
    :instruction-text="formInstruction"
    :awaiting-age="awaitingAge"
  >
    <div v-if="formLoading" class="ct-form-loading">Corrigiendo…</div>
    <keep-alive>
      <component
        v-if="!awaitingAge"
        :is="formComponent"
        :key="test.slug"
        ref="activeForm"
        :test-value="test.value"
        :age="age"
        :initial-answers="initialAnswers"
        @corrected="onCorrected"
        @toolbar-meta="onToolbarMeta"
      />
    </keep-alive>
  </TestFormPanel>
</template>

<script>
import TestFormPanel from "./TestFormPanel.vue";
import TeprosifForm from "../forms/TeprosifForm.vue";
import TecalForm from "../forms/TecalForm.vue";
import StsgForm from "../forms/StsgForm.vue";
import IdtelForm from "../forms/IdtelForm.vue";
import WechslerForm from "../forms/WechslerForm.vue";
import IcapForm from "../forms/IcapForm.vue";
import SensorialForm from "../forms/SensorialForm.vue";
import LegacyTestForm from "../forms/LegacyTestForm.vue";
import { FONO_MARK_INCORRECTS_HINT } from "../constants/fonoInstructions";

// Los formularios de los tests 18-24 se cargan recién al elegir uno: sus
// ítems no tienen por qué viajar en el bundle común de todas las páginas.
const CuestionarioSensorialForm = () =>
  import(/* webpackChunkName: "corregir-test-cuestionarios" */ "../forms/CuestionarioSensorialForm.vue");
const CarasForm = () =>
  import(/* webpackChunkName: "corregir-test-caras" */ "../forms/CarasForm.vue");

const CUSTOM_FORMS = {
  teprosif: TeprosifForm,
  tecal: TecalForm,
  stsg: StsgForm,
  idtel: IdtelForm,
  wais4: WechslerForm,
  wisc5: WechslerForm,
  icap: IcapForm,
  "caras-r": CarasForm,
  conners: CuestionarioSensorialForm,
  "sensorial-padres": SensorialForm,
  "sensorial-escolar": SensorialForm,
  "sensorial-adultos": CuestionarioSensorialForm,
  "spm2-preescolar": CuestionarioSensorialForm,
  "spm2-nino": CuestionarioSensorialForm,
  "spm2-adolescente": CuestionarioSensorialForm,
  "spm2-autorreporte": CuestionarioSensorialForm,
};

export default {
  name: "TestFormRouter",
  components: { TestFormPanel },
  props: {
    test: { type: Object, default: null },
    age: { type: String, default: "" },
    // Respuestas ya enviadas a RedPsi de una corrección anterior, para editarla
    // (ver CorregirTestWorkspace.startEdit). Cada form la consume en su propio
    // `created()` — el workspace remonta este router al entrar en edición, así
    // que la prop siempre llega antes de que el form exista.
    initialAnswers: { type: Object, default: null },
  },
  data() {
    return {
      toolbarMeta: {
        label: "",
        disabled: false,
        loading: false,
      },
    };
  },
  computed: {
    formComponent() {
      if (!this.test) return null;
      return CUSTOM_FORMS[this.test.slug] || LegacyTestForm;
    },
    formLoading() {
      return this.toolbarMeta.loading;
    },
    formInstruction() {
      if (!this.test || this.test.professionId !== "fono") return "";
      return FONO_MARK_INCORRECTS_HINT;
    },
    awaitingAge() {
      return Boolean(this.test && this.test.requiresAge && !this.age);
    },
  },
  watch: {
    test() {
      this.toolbarMeta = { label: "", disabled: false, loading: false };
      this.$nextTick(() => this.syncToolbarFromForm());
    },
    age() {
      this.$nextTick(() => this.syncToolbarFromForm());
    },
  },
  mounted() {
    this.syncToolbarFromForm();
  },
  methods: {
    syncToolbarFromForm() {
      const form = this.$refs.activeForm;
      if (form && form.toolbarLabel) {
        this.onToolbarMeta({
          label: form.toolbarLabel,
          disabled: form.toolbarDisabled,
          loading: form.loading,
        });
      } else if (this.test) {
        this.onToolbarMeta({
          label: this.test.correctActionLabel,
          disabled: !this.age && this.test.requiresAge,
          loading: false,
        });
      }
    },
    onToolbarMeta(meta) {
      this.toolbarMeta = { ...this.toolbarMeta, ...meta };
      this.$emit("toolbar-meta", this.toolbarMeta);
    },
    submit() {
      const form = this.$refs.activeForm;
      if (form && typeof form.submit === "function") {
        form.submit();
      }
    },
    onCorrected(result) {
      this.$emit("corrected", result);
    },
    resetActiveForm() {
      const form = this.$refs.activeForm;
      if (form && typeof form.reset === "function") form.reset();
    },
  },
};
</script>
